/* seoInventory.js
 *
 * Single source of truth for the public site's "All Pages" inventory.
 *
 * Powers two surfaces:
 *   1. Footer's collapsible inventory list (Footer.jsx)
 *   2. The HTML sitemap landing page at /sitemap (SitemapPage.jsx)
 *
 * The HTML sitemap is the highest-impact indexation lever for
 * BuildPilot: it consolidates ~400 programmatic pages into a single
 * crawlable surface so Google can discover every URL through one click
 * from the homepage footer. Industry data shows this lifts indexation
 * rate by 20-30 percentage points on programmatic-heavy sites within
 * 60 days.
 *
 * Convention: add new sections here, not in Footer.jsx. Every URL
 * must be a real route declared in App.js or a real prerendered page.
 */

export const SEO_INVENTORY = {
  "How we help": [
    { name: "Building a new home", href: "/building-a-new-home" },
    { name: "Choosing land", href: "/choosing-land" },
    { name: "Renovations", href: "/renovations" },
    { name: "Knockdown rebuild", href: "/knockdown-rebuild" },
    { name: "Changing builders", href: "/changing-builders" },
    { name: "Property development", href: "/property-development" },
    { name: "Inspection coordination", href: "/building-inspectors" },
    { name: "Reliable builders (transparency)", href: "/reliable-builders-adelaide" },
    { name: "Building partners", href: "/building-partners" },
    { name: "PayLocker for homeowners", href: "/partners/paylocker" },
    { name: "PayLocker for builders & businesses", href: "/partners/paylocker-for-business" },
  ],
  "Builders": [
    { name: "Best home builders Adelaide", href: "/home-builders-adelaide" },
    { name: "Compare Adelaide builders", href: "/compare" },
    { name: "Builders near me", href: "/home-builders-near-me-adelaide" },
    { name: "Custom home builders", href: "/custom-home-builders-adelaide" },
    { name: "Volume vs project", href: "/volume-builder-vs-project-builder-adelaide" },
    { name: "Volume vs custom", href: "/volume-builder-vs-custom-builder-adelaide" },
    { name: "Project vs custom", href: "/project-builder-vs-custom-builder-adelaide" },
    { name: "Luxury home builders", href: "/luxury-home-builders-adelaide" },
    { name: "Budget home builders", href: "/budget-home-builders-adelaide" },
    { name: "Top 5 home builders", href: "/top-5-home-builders-adelaide" },
    { name: "Reliable builders", href: "/reliable-builders-adelaide" },
    { name: "Adelaide builders list", href: "/adelaide-builders-list" },
    { name: "Granny flat builders", href: "/granny-flat-builders-adelaide" },
    { name: "Townhouse builders", href: "/townhouse-builders-adelaide" },
    { name: "Small home builders", href: "/small-home-builders-adelaide" },
    { name: "Transportable homes", href: "/transportable-homes-adelaide" },
    { name: "Country home builders", href: "/country-home-builders-south-australia" },
    { name: "Dual occupancy Adelaide", href: "/dual-occupancy-adelaide" },
  ],
  "By region": [
    { name: "Northern Adelaide", href: "/home-builders-northern-adelaide" },
    { name: "Southern Adelaide", href: "/home-builders-southern-adelaide" },
    { name: "Adelaide Hills", href: "/home-builders-adelaide-hills" },
    { name: "Western Adelaide", href: "/home-builders-western-adelaide" },
    { name: "All suburbs", href: "/south-australia/adelaide/areas" },
    { name: "Adelaide build a new home", href: "/adelaide/build-a-new-home" },
    { name: "South Australia", href: "/south-australia" },
    { name: "Onkaparinga Council", href: "/building-in-onkaparinga-council" },
    { name: "Playford Council", href: "/building-in-playford-council" },
    { name: "Salisbury Council", href: "/building-in-salisbury-council" },
    { name: "Tea Tree Gully Council", href: "/building-in-tea-tree-gully-council" },
    { name: "Charles Sturt Council", href: "/building-in-charles-sturt-council" },
    { name: "Mount Barker Council", href: "/building-in-mount-barker-council" },
    { name: "Land estates", href: "/estates" },
    { name: "Growth suburbs", href: "/growth-suburbs-adelaide" },
  ],
  "Costs & budgets": [
    { name: "Adelaide build cost report 2026", href: "/adelaide-build-cost-report-2026" },
    { name: "Building costs Adelaide 2026", href: "/building-costs-adelaide-2026" },
    { name: "House building costs by bedroom", href: "/house-building-costs-by-bedroom-adelaide" },
    { name: "$500K builds", href: "/what-can-you-build-for-500k-adelaide" },
    { name: "$600K builds", href: "/what-can-you-build-for-600k-adelaide" },
    { name: "$650K builds", href: "/what-can-you-build-for-650k-adelaide" },
    { name: "$750K builds", href: "/what-can-you-build-for-750k-adelaide" },
    { name: "$850K builds", href: "/what-can-you-build-for-850k-adelaide" },
    { name: "Site costs", href: "/choosing-land/site-costs" },
    { name: "Build budget guide", href: "/building-a-new-home/budget" },
    { name: "Renovation costs", href: "/renovations/costs" },
    { name: "Knockdown rebuild costs", href: "/knockdown-rebuild/costs" },
    { name: "Cost to build (Adelaide)", href: "/help/cost-to-build-house-adelaide" },
    { name: "Build cost benchmark (PDF)", href: "/lead-magnets/build-cost-benchmark" },
  ],
  "House & land": [
    { name: "All live H&L packages →", href: "/house-and-land-adelaide" },
    { name: "3 bedroom packages", href: "/house-and-land-adelaide/filter/3-bedroom" },
    { name: "4 bedroom packages", href: "/house-and-land-adelaide/filter/4-bedroom" },
    { name: "Single storey", href: "/house-and-land-adelaide/filter/single-storey" },
    { name: "Double storey", href: "/house-and-land-adelaide/filter/double-storey" },
    { name: "First home buyer packages", href: "/house-and-land-adelaide/filter/first-home-buyer" },
    { name: "Under $700K", href: "/house-and-land-adelaide/filter/under-700k" },
    { name: "Under $500K", href: "/house-and-land-adelaide/filter/under-500k" },
    { name: "$500K - $700K", href: "/house-and-land-adelaide/filter/500k-700k" },
    { name: "$700K - $800K", href: "/house-and-land-adelaide/filter/700k-800k" },
    { name: "$800K+", href: "/house-and-land-adelaide/filter/800k-plus" },
    { name: "Small blocks (< 350m²)", href: "/house-and-land-adelaide/filter/small-blocks" },
    { name: "Large blocks (700m²+)", href: "/house-and-land-adelaide/filter/large-blocks" },
    { name: "Land for sale", href: "/land-for-sale-adelaide" },
    { name: "House and land Adelaide (guide)", href: "/house-and-land-packages-adelaide" },
    { name: "First home buyer H&L guide", href: "/first-home-buyer-house-and-land-guide" },
    { name: "Turnkey vs standard", href: "/turnkey-vs-standard-packages" },
    { name: "House and land hidden costs", href: "/house-and-land-package-hidden-costs" },
    { name: "House and land red flags", href: "/house-and-land-red-flags" },
    { name: "Questions to ask H&L", href: "/questions-to-ask-house-and-land" },
    { name: "Cheapest H&L", href: "/cheapest-house-and-land-packages-adelaide" },
    { name: "Two Wells H&L", href: "/house-and-land-packages-two-wells" },
    { name: "House & land vs buying", href: "/house-and-land-vs-buying-separately" },
  ],
  "Designs & display homes": [
    { name: "Home improvement hub", href: "/home-improvement" },
    { name: "Home designs", href: "/home-designs" },
    { name: "Display homes Adelaide", href: "/display-homes-adelaide" },
    { name: "Display homes Adelaide Hills", href: "/display-homes-adelaide-hills" },
    { name: "Display homes Munno Para", href: "/display-homes-munno-para" },
    { name: "Display home guide", href: "/display-home-guide" },
    { name: "Display home checklist", href: "/display-home-checklist" },
    { name: "Single vs double storey", href: "/single-vs-double-storey" },
    { name: "Two storey home builder", href: "/best-builder-for-two-storey-homes" },
    { name: "Acreage builder", href: "/best-builder-for-acreage" },
    { name: "First home builder", href: "/best-builder-for-first-home-buyers" },
    { name: "KDR builder", href: "/best-builder-for-knockdown-rebuild" },
    { name: "Verandahs & pergolas", href: "/verandahs-pergolas" },
    { name: "Sheds & garages", href: "/sheds-garages" },
    { name: "Decking", href: "/decking" },
    { name: "Swimming pools", href: "/swimming-pools" },
    { name: "Landscaping", href: "/landscaping" },
    { name: "Interior design", href: "/interior-design" },
    { name: "Home design", href: "/home-design" },
  ],
  "Process & timeline": [
    { name: "Build timeline", href: "/building-a-new-home/build-timeline" },
    { name: "Choosing a builder", href: "/building-a-new-home/choosing-a-builder" },
    { name: "Building contracts", href: "/building-a-new-home/contracts" },
    { name: "Build timeline phase", href: "/building-a-new-home/house-and-land-packages" },
    { name: "Build times Adelaide", href: "/build-times-adelaide" },
    { name: "Best time to build", href: "/best-time-to-start-building-adelaide" },
    { name: "Summer vs winter", href: "/building-in-summer-vs-winter" },
    { name: "Common builder problems", href: "/common-builder-problems" },
    { name: "Builder went bankrupt", href: "/builder-went-bankrupt-what-now" },
    { name: "Change builders mid-build", href: "/how-to-change-builders-mid-build" },
    { name: "Dispute with builder", href: "/dispute-with-builder-adelaide" },
    { name: "Questions to ask builders", href: "/questions-to-ask-builders" },
    { name: "Which builder should I choose", href: "/which-builder-should-i-choose" },
    { name: "Ready to build checklist", href: "/ready-to-build-checklist" },
    { name: "Builder shortlist scorecard (PDF)", href: "/lead-magnets/builder-shortlist-scorecard" },
    { name: "Settlement timeline (PDF)", href: "/lead-magnets/settlement-timeline" },
  ],
  "Knockdown rebuild": [
    { name: "Knockdown rebuild guide", href: "/knockdown-rebuild" },
    { name: "Long-form KDR guide", href: "/knockdown-rebuild-guide" },
    { name: "KDR costs", href: "/knockdown-rebuild/costs" },
    { name: "When to KDR vs renovate", href: "/knockdown-rebuild/when-to-knockdown-vs-renovate" },
    { name: "KDR council approvals", href: "/knockdown-rebuild/council-approvals" },
    { name: "Demolition process", href: "/knockdown-rebuild/demolition-process" },
    { name: "New build vs KDR", href: "/new-build-vs-knockdown-rebuild" },
  ],
  "Land & soil": [
    { name: "Choosing land", href: "/choosing-land" },
    { name: "Site costs", href: "/choosing-land/site-costs" },
    { name: "Soil types", href: "/choosing-land/soil-types" },
    { name: "Block assessment", href: "/choosing-land/block-assessment" },
    { name: "Cheapest land in SA", href: "/help/cheapest-land-south-australia" },
    { name: "Cheapest land right now", href: "/help/cheapest-land-to-buy-right-now" },
    { name: "Buy land off SA government", href: "/help/buy-land-off-sa-government" },
    { name: "Building vs buying", href: "/building-vs-buying" },
    { name: "Build vs buy Adelaide", href: "/build-vs-buy-adelaide" },
    { name: "Are house prices falling", href: "/help/are-house-prices-falling-adelaide" },
  ],
  "Help & advice": [
    { name: "All advice articles", href: "/help" },
    { name: "Best builder in SA", href: "/help/best-home-builder-south-australia" },
    { name: "Most reliable builder", href: "/help/most-reliable-builder-adelaide" },
    { name: "Best reputation builder", href: "/help/which-house-builder-has-the-best-reputation" },
    { name: "Top 5 construction companies", href: "/help/top-5-construction-companies" },
    { name: "Most expensive part of building", href: "/help/most-expensive-part-of-building-a-house" },
    { name: "Best to buy or build", href: "/help/best-to-buy-or-build-adelaide" },
    { name: "Best time of year to build", href: "/help/best-time-of-year-to-build" },
    { name: "Build a house for $600,000", href: "/help/build-house-for-600000" },
    { name: "Cost to build house Adelaide", href: "/help/cost-to-build-house-adelaide" },
    { name: "How much for H&L package", href: "/help/how-much-money-house-and-land-package" },
    { name: "Are H&L packages worth it", href: "/help/house-and-land-packages-worth-it" },
    { name: "Suburbs that will boom", href: "/help/which-adelaide-suburbs-will-boom" },
    { name: "Adelaide suburbs to boom", href: "/help/which-suburbs-will-boom-in-adelaide" },
    { name: "Build or buy in Australia", href: "/help/is-it-cheaper-to-build-or-buy-a-house-australia" },
  ],
  "Reference": [
    { name: "Building glossary", href: "/terminology" },
    { name: "Building tips & tricks", href: "/buildingtipsandtricks" },
    { name: "Our community", href: "/community" },
    { name: "FAQs", href: "/faqs" },
    { name: "Building questions", href: "/building-questions-adelaide" },
    { name: "Complete building guide", href: "/complete-guide-building-adelaide" },
    { name: "Real Adelaide build stories", href: "/case-studies" },
    { name: "Awards", href: "/awards" },
    { name: "As seen in", href: "/media" },
    { name: "Press release: BuildPilot — Australia's Home for Building", href: "/media/buildpilot-launch-australias-home-for-building" },
    { name: "Results", href: "/results" },
    { name: "Editorial process", href: "/about/editorial-process" },
    { name: "Editorial team", href: "/about/editorial-team" },
    { name: "Source Index (authorities we cite)", href: "/sources" },
    { name: "Editorial Notebook (our quarterly opinions)", href: "/editorial-notebook" },
    { name: "Verify a business", href: "/verify-a-business" },
    { name: "Hiring trades guide", href: "/hiring-trades-guide" },
    { name: "Hiring professionals guide", href: "/hiring-professionals-guide" },
    { name: "Market update", href: "/market-update" },
    { name: "Builder quiz", href: "/builder-quiz" },
  ],
  "Compare the options": [
    { name: "Compare the options (hub)", href: "/compare-options" },
    { name: "All build choices", href: "/build-choices" },
    { name: "Colorbond vs roof tiles", href: "/build-choices/colorbond-vs-roof-tiles" },
    { name: "Concrete vs terracotta roof tiles", href: "/build-choices/colorbond-vs-roof-tiles#concrete-vs-terracotta-roof-tiles" },
    { name: "Metal vs PVC gutters", href: "/build-choices/colorbond-vs-roof-tiles#metal-vs-pvc-gutters" },
    { name: "Render vs brick vs cladding", href: "/build-choices/render-vs-brick-vs-cladding" },
    { name: "Weatherboard vs brick", href: "/build-choices/render-vs-brick-vs-cladding#weatherboard-vs-brick" },
    { name: "Fibre cement vs timber cladding", href: "/build-choices/render-vs-brick-vs-cladding#fibre-cement-vs-timber-cladding" },
    { name: "Brick veneer vs double brick", href: "/build-choices/brick-veneer-vs-double-brick" },
    { name: "Single vs double brick", href: "/build-choices/brick-veneer-vs-double-brick#single-vs-double-brick" },
    { name: "Window frames: aluminium vs timber vs uPVC", href: "/build-choices/window-frames-aluminium-timber-upvc" },
    { name: "Bifold vs sliding vs stacker doors", href: "/build-choices/window-frames-aluminium-timber-upvc#bifold-vs-sliding-vs-stacker-doors" },
    { name: "Thermally broken vs standard aluminium", href: "/build-choices/window-frames-aluminium-timber-upvc#thermally-broken-vs-standard-aluminium" },
    { name: "Is double glazing worth it", href: "/build-choices/double-glazing-worth-it" },
    { name: "Double vs triple glazing", href: "/build-choices/double-glazing-worth-it#double-vs-triple-glazing" },
    { name: "Insulation types explained", href: "/build-choices/insulation-types-explained" },
    { name: "Bulk vs reflective insulation", href: "/build-choices/insulation-types-explained#bulk-vs-reflective-insulation" },
    { name: "Ceiling insulation R5 vs R6", href: "/build-choices/insulation-types-explained#ceiling-insulation-r5-vs-r6" },
    { name: "All-electric vs gas home", href: "/build-choices/all-electric-vs-gas-home" },
    { name: "Ducted vs split-system air conditioning", href: "/build-choices/all-electric-vs-gas-home#ducted-vs-split-system-air-conditioning" },
    { name: "Induction vs gas cooktop", href: "/build-choices/all-electric-vs-gas-home#induction-vs-gas-cooktop" },
    { name: "Evaporative vs refrigerated cooling", href: "/build-choices/all-electric-vs-gas-home#evaporative-vs-refrigerated-cooling" },
    { name: "Flooring by room", href: "/build-choices/flooring-by-room" },
    { name: "Hybrid vs laminate flooring", href: "/build-choices/flooring-by-room#hybrid-vs-laminate-flooring" },
    { name: "Engineered vs solid timber flooring", href: "/build-choices/flooring-by-room#engineered-vs-solid-timber-flooring" },
    { name: "Porcelain vs ceramic tiles", href: "/build-choices/flooring-by-room#porcelain-vs-ceramic-tiles" },
    { name: "Driveway & garage finishes", href: "/build-choices/garage-and-driveway-finishes" },
    { name: "Engineered-stone alternatives vs natural stone", href: "/kitchen-benchtops-sinks-tapware-guide#engineered-stone-alternatives" },
    { name: "Porcelain vs laminate benchtops", href: "/kitchen-benchtops-sinks-tapware-guide#porcelain-vs-laminate-benchtop" },
    { name: "Stone vs timber benchtops", href: "/kitchen-benchtops-sinks-tapware-guide#stone-vs-timber-benchtop" },
    { name: "Freestanding vs built-in bath", href: "/shower-screens-buying-guide#freestanding-vs-built-in-bath" },
    { name: "Walk-in vs enclosed showers", href: "/shower-screens-buying-guide#walk-in-vs-enclosed-shower" },
    { name: "Wall-hung vs floor-standing vanity", href: "/shower-screens-buying-guide#wall-hung-vs-floor-vanity" },
    { name: "Floor-to-ceiling vs half-height tiles", href: "/shower-screens-buying-guide#floor-to-ceiling-vs-half-tiles" },
    { name: "Hollow-core vs solid-core doors", href: "/new-home-fit-out-guide#hollow-vs-solid-core-doors" },
    { name: "Flush vs panel internal doors", href: "/new-home-fit-out-guide#flush-vs-panel-doors" },
    { name: "Cornice vs square-set ceilings", href: "/new-home-fit-out-guide#cornice-vs-square-set" },
    { name: "Timber vs MDF skirting", href: "/new-home-fit-out-guide#timber-vs-mdf-skirting" },
    { name: "Built-in wardrobe vs walk-in robe", href: "/new-home-fit-out-guide#built-in-vs-walk-in-robe" },
    { name: "Timber vs concrete stairs", href: "/new-home-fit-out-guide#timber-vs-concrete-stairs" },
    { name: "Aluminium vs Colorbond fencing", href: "/fencing-adelaide#aluminium-vs-colorbond-fencing" },
    { name: "Concrete vs timber retaining sleepers", href: "/landscaping-adelaide#concrete-vs-timber-sleepers" },
    { name: "Artificial turf vs natural grass", href: "/landscaping-adelaide#artificial-turf-vs-natural-grass" },
  ],
  "Suppliers": [
    { name: "All suppliers", href: "/suppliers" },
    { name: "Kitchens & appliances", href: "/find/supplier/kitchens-appliances" },
    { name: "Bathrooms & tapware", href: "/find/supplier/bathrooms-tapware" },
    { name: "Tiles", href: "/find/supplier/tiles" },
    { name: "Flooring", href: "/find/supplier/flooring" },
    { name: "Stone & benchtops", href: "/find/supplier/stone-benchtops" },
    { name: "Windows & doors", href: "/find/supplier/windows-doors" },
    { name: "Roofing & cladding", href: "/find/supplier/roofing-cladding" },
    { name: "Lighting", href: "/find/supplier/lighting" },
    { name: "Solar & batteries", href: "/find/supplier/solar-batteries" },
    { name: "Outdoor kitchens", href: "/find/supplier/outdoor-kitchens" },
  ],
  "Tradies": [
    { name: "All tradies", href: "/find/category/tradies" },
    { name: "Electricians", href: "/find/electrician" },
    { name: "Plumbers", href: "/find/plumber" },
    { name: "Concretors", href: "/find/concretor" },
    { name: "Carpenters", href: "/find/carpenter" },
    { name: "Painters", href: "/find/painter" },
    { name: "Tilers", href: "/find/tiler" },
    { name: "Cabinet makers", href: "/find/cabinet-maker" },
    { name: "Roofers", href: "/find/roofer" },
    { name: "Plasterers", href: "/find/plasterer" },
    { name: "Stonemasons", href: "/find/stonemason" },
    { name: "Landscapers", href: "/find/landscaper" },
    { name: "Hiring trades guide", href: "/hiring-trades-guide" },
  ],
  "Professional services": [
    { name: "All professionals", href: "/find/category/professionals" },
    { name: "Building inspectors", href: "/building-inspectors" },
    { name: "Conveyancers", href: "/find/conveyancer" },
    { name: "Mortgage brokers", href: "/find/mortgage-broker" },
    { name: "Architects & designers", href: "/find/architect" },
    { name: "Surveyors", href: "/find/surveyor" },
    { name: "Engineers", href: "/find/engineer" },
    { name: "Town planners", href: "/find/town-planner" },
    { name: "Lawyers", href: "/find/lawyer" },
    { name: "Hiring professionals guide", href: "/hiring-professionals-guide" },
  ],
  "Workspace tools": [
    { name: "Find a Builder", href: "/find-a-builder" },
    { name: "Cost Tracker", href: "/workspace/tools/cost-tracker" },
    { name: "Quote comparison", href: "/workspace/tools/quote-comparison" },
    { name: "Build progress / defects", href: "/workspace/tools/build-progress" },
    { name: "Facade visualiser", href: "/workspace/tools/facade-visualiser" },
    { name: "Project intelligence", href: "/workspace/tools/intelligence" },
    { name: "Milestones", href: "/workspace/tools/milestones" },
    { name: "Checklists", href: "/workspace/tools/checklists" },
    { name: "Documents", href: "/workspace/tools/documents" },
    { name: "Contacts hub", href: "/workspace/tools/contacts" },
    { name: "Builder licence check", href: "/workspace/tools/builder-licence-check" },
    { name: "Building insurance check", href: "/workspace/tools/building-insurance-check" },
    { name: "Government resources", href: "/workspace/tools/government-resources" },
    { name: "Solar orientation", href: "/workspace/tools/solar-orientation" },
    { name: "House & land matchmaker", href: "/workspace/tools/house-and-land-matchmaker" },
    { name: "Favourite homes", href: "/workspace/tools/favourite-homes" },
  ],
  "Legal": [
    { name: "Terms of Use", href: "/terms" },
    { name: "Privacy", href: "/privacy" },
    { name: "How recommendations work", href: "/legal/recommendations" },
    { name: "Advertising & featured placements", href: "/legal/advertising" },
    { name: "Commercial disclosure", href: "/legal/commercial-disclosure" },
    { name: "Property listings", href: "/legal/property-listings" },
    { name: "How we make money", href: "/about/how-we-make-money" },
    { name: "One to One Support Agreement", href: "/legal/one-to-one-support-agreement" },
    { name: "Matching policy", href: "/legal/matching-policy" },
    { name: "Complaints", href: "/legal/complaints-process" },
    { name: "Copyright", href: "/copyright" },
    { name: "Cookies", href: "/cookies" },
    { name: "App terms", href: "https://buildpilot.app/terms", external: true },
  ],
};

export const getTotalInventoryCount = () =>
  Object.values(SEO_INVENTORY).reduce((sum, group) => sum + group.length, 0);
